/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { loadStyleSheet } from 'src/hooks/loadStylesheet';
import { getOtkV2AtStylesUrl, getOtkV2ReaderUrl } from 'src/services/urls';
import { loadScript } from 'src/hooks/loadScript';
import classnames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import './PlayerContainer.scss';

// Split code with `React.lazy` to lazy-load your container components
const Player = React.lazy(() => import('./Player'));

const readerWarningContainer = 'reader-warning-container';
const readerWarningAlert = 'reader-warning-alert';
const readerWarningMessage = 'reader-warning-message';
const playerLogoutContainer = 'player-logout-container';

const getErrorMessage = (readerVersion: string | null) => {
	let errorMessage =
		'Error when trying to load the latest reader version. Please refresh the web page.';
	if (readerVersion) {
		errorMessage = `Error when trying to load the reader version ${readerVersion}. Please use the correct version and refresh the web page.`;
	}
	return (
		<div>
			<span className={readerWarningAlert}>Alert: </span>
			<span className={readerWarningMessage}>{errorMessage}</span>
		</div>
	);
};

function PlayerContainer() {
	const history = useHistory();
	const location = useLocation<{
		readerVersion?: string;
		showLogoutButton?: boolean;
	}>();

	const { showLogoutButton = true, readerVersion: locationReaderVersion = null } =
		location.state || {};

	const [readerVersion, setReaderVersion] = useState(locationReaderVersion);
	const [isLoaded, setIsLoaded] = useState(false);
	const [lastFailedReaderVersion, setLastFailedReaderVersion] = useState<string | null>(null);

	const handleLogout = useCallback(() => {
		history.replace('/');
	}, [history]);

	const load = useCallback(async () => {
		const readerUrl = getOtkV2ReaderUrl(readerVersion);
		console.info('Loading OTK v2 reader', { readerVersion, readerUrl });
		const readerScriptPromise = loadScript(`${readerUrl}.js`);
		const readerCssPromise = loadStyleSheet(`${readerUrl}.css`);

		try {
			await Promise.all([readerScriptPromise, readerCssPromise]);
			await loadStyleSheet(getOtkV2AtStylesUrl());
			setIsLoaded(true);
		} catch (error) {
			console.error('Failed to load OTK v2 reader', { readerVersion, readerUrl, error });
			setLastFailedReaderVersion(readerVersion);
			setReaderVersion(null);
			setIsLoaded(false);
			handleLogout();
		}
	}, [handleLogout, readerVersion]);

	useEffect(() => {
		load();
	}, [load]);

	return (
		<div
			className={classnames({
				[readerWarningContainer]: lastFailedReaderVersion,
			})}
		>
			{lastFailedReaderVersion && getErrorMessage(lastFailedReaderVersion)}
			{showLogoutButton && (
				<div className={playerLogoutContainer}>
					<button onClick={handleLogout}>Logout</button>
				</div>
			)}
			{isLoaded && <Player />}
		</div>
	);
}

export default PlayerContainer;
